import {
    Box,
    Link,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Typography,
    Divider,
    IconButton,
    Stack,
    Tooltip,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import BaseModal from '@/components/modals/BaseModal';
import { close } from '@/store/uiSlice.ts';
import { useI18nContext } from '@/i18n/i18n-react';

const DOWNLOADED_STORAGE_KEY = 'downloaded_files';

const safeReadDownloadedMap = (): Record<string, number> => {
    try {
        const raw = localStorage.getItem(DOWNLOADED_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object') return {};
        return parsed as Record<string, number>;
    } catch {
        return {};
    }
};

const safeWriteDownloadedMap = (map: Record<string, number>) => {
    try {
        localStorage.setItem(DOWNLOADED_STORAGE_KEY, JSON.stringify(map));
    } catch {
        // ignore (private mode / storage disabled)
    }
};

const fileKey = (title: string, url: string) => `${title}::${url}`;

const formatBytes = (bytes?: number) => {
    if (bytes == null || Number.isNaN(bytes)) return '';
    if (bytes === 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, i);

    return `${value >= 10 || i === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[i]}`;
};

const getFileMeta = (url: string) => {
    try {
        const u = new URL(url, window.location.origin);
        const filename = (u.pathname.split('/').pop() ?? '').trim();
        const ext = filename.includes('.') ? filename.split('.').pop() : '';
        return {
            host: u.host,
            ext: ext ? ext.toUpperCase() : '',
        };
    } catch {
        // If url isn't parsable (relative/invalid), fall back to simple heuristics
        const cleaned = url.split('?')[0];
        const filename = cleaned.split('/').pop() ?? '';
        const ext = filename.includes('.') ? filename.split('.').pop() : '';
        return {
            host: '',
            ext: ext ? ext.toUpperCase() : '',
        };
    }
};

const FilesModal = () => {
    const dispatch = useAppDispatch();
    const open = useAppSelector((state) => state.ui.open.files);
    const onClose = () => dispatch(close('files'));
    const { LL } = useI18nContext();

    const files = useAppSelector((state) => state.modal.files);

    const [downloadedMap, setDownloadedMap] = useState<Record<string, number>>({});

    // Load persisted state when modal opens.
    useEffect(() => {
        if (!open) return;
        setDownloadedMap(safeReadDownloadedMap());
    }, [open]);

    const downloadedCount = useMemo(() => {
        if (!files.length) return 0;
        return files.reduce((acc, f) => acc + (downloadedMap[fileKey(f.title, f.url)] ? 1 : 0), 0);
    }, [files, downloadedMap]);

    const markDownloaded = (title: string, url: string) => {
        const key = fileKey(title, url);
        setDownloadedMap((prev) => {
            if (prev[key]) return prev;
            const next = { ...prev, [key]: Date.now() };
            safeWriteDownloadedMap(next);
            return next;
        });
    };

    const resetDownloaded = () => {
        setDownloadedMap({});
        safeWriteDownloadedMap({});
    };

    return (
        <BaseModal open={open} onClose={onClose} title={LL.filesModal.title()}>
            <Box sx={{ width: '100%' }}>
                <Stack
                    direction="row"
                    sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        {LL.filesModal.subtitle()} — {downloadedCount}/{files.length}
                    </Typography>
                    <Tooltip title="Reset downloaded status">
                        <span>
                            <IconButton
                                size="small"
                                onClick={resetDownloaded}
                                disabled={files.length === 0 && downloadedCount === 0}
                            >
                                <RestartAltIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>

                {files.length === 0 ? (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        {LL.filesModal.noFiles()}
                    </Typography>
                ) : (
                    <List disablePadding>
                        {files.map((file, idx) => {
                            const params = new URLSearchParams({
                                name: btoa(file.title),
                                url: file.url,
                            });

                            const { host, ext } = getFileMeta(file.url);
                            const size = formatBytes((file as unknown as { size?: number }).size);
                            const isDownloaded = Boolean(
                                downloadedMap[fileKey(file.title, file.url)]
                            );

                            return (
                                <Box key={idx}>
                                    <ListItem
                                        disableGutters
                                        secondaryAction={
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{ alignItems: 'center' }}
                                            >
                                                {size ? (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            minWidth: 72,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {size}
                                                    </Typography>
                                                ) : null}
                                                {isDownloaded ? (
                                                    <Tooltip title="Downloaded">
                                                        <CheckCircleIcon
                                                            fontSize="small"
                                                            sx={{ color: 'success.main' }}
                                                        />
                                                    </Tooltip>
                                                ) : null}
                                            </Stack>
                                        }
                                    >
                                        <Link
                                            href={`rest/Download?${params.toString()}`}
                                            target="_blank"
                                            underline="none"
                                            color="inherit"
                                            onClick={() => markDownloaded(file.title, file.url)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '100%',
                                                pr: size || isDownloaded ? 10 : 0,
                                            }}
                                        >
                                            <ListItemAvatar sx={{ minWidth: 48 }}>
                                                <Avatar
                                                    variant="rounded"
                                                    src={file.preview}
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        bgcolor: isDownloaded
                                                            ? 'grey.300'
                                                            : 'primary.main',
                                                        color: isDownloaded
                                                            ? 'text.secondary'
                                                            : 'primary.contrastText',
                                                    }}
                                                >
                                                    <InsertDriveFileIcon fontSize="small" />
                                                </Avatar>
                                            </ListItemAvatar>

                                            <ListItemText
                                                primary={file.title}
                                                secondary={
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            flexWrap: 'wrap',
                                                        }}
                                                    >
                                                        {ext && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                }}
                                                            >
                                                                {ext}
                                                            </Typography>
                                                        )}
                                                        {host && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                }}
                                                            >
                                                                {host}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        </Link>
                                    </ListItem>
                                    {idx < files.length - 1 && <Divider component="li" />}
                                </Box>
                            );
                        })}
                    </List>
                )}
            </Box>
        </BaseModal>
    );
};

export default FilesModal;
